import { forceCenter, forceLink, forceManyBody, forceSimulation } from "d3-force-3d";
import { HeadNode } from "./nodes-and-links-models";

export type NodePosition = { x: number; y: number; z: number };

export type NodePositionMap = { [id: string]: NodePosition };

export function origin(extraProps: Record<never, never> = {}): NodePosition {
  return { x: 0, y: 0, z: 0, ...extraProps };
}

export function singleNodeCentered(
  nodes: HeadNode[],
  center: NodePosition = origin(),
): NodePositionMap {
  if (nodes.length > 1) {
    throw new Error(`Expected a single node but got ${nodes.length}`);
  }
  const positions = {} as NodePositionMap;
  const { x, y, z } = center;
  positions[nodes[0].id] = { x, y, z };
  return positions;
}

export function linedUpHorizontally(
  nodes: HeadNode[],
  width = 200,
  center: NodePosition = origin(),
): NodePositionMap {
  const spacePerNode = width / nodes.length;
  const startingPoint = center.x - width / 2;
  const positions = nodes.reduce((obj, node, i) => {
    obj[node.id] = {
      x: startingPoint + i * spacePerNode,
      y: center.y,
      z: center.z,
    };
    return obj;
  }, {});

  return positions;
}

export function teamAroundIndividual(
  centerId: string,
  nodes: HeadNode[],
  radius: number,
  center: NodePosition = origin(),
): NodePositionMap {
  const radiansPerNode = (Math.PI * 2) / (nodes.length - 1);
  let hasPositionedCenterNode = false;
  const positions = nodes.reduce((obj, node, i) => {
    if (node.id === centerId) {
      obj[node.id] = {
        x: center.x,
        y: center.y,
        z: center.z,
      };
      hasPositionedCenterNode = true;
    } else {
      const j = hasPositionedCenterNode ? i - 1 : i;
      obj[node.id] = {
        x: center.x + radius * Math.sin(j * radiansPerNode),
        y: center.y + radius * Math.cos(j * radiansPerNode),
        z: center.z,
      };
    }
    return obj;
  }, {});

  return positions;
}

export function teamAroundIndividualLeaderTypeLayout(
  centerId: string,
  nodes: HeadNode[],
  links: any[],
  soloOrViewpoints: "solo" | "viewpoints" = "solo",
  center: NodePosition = origin(),
): NodePositionMap {
  const distanceFactor = 1.8;
  const startingRadians = nodes.length > 1 ? Math.PI / (nodes.length - 1) : 0;
  const radiansPerNode = (Math.PI * 2) / (nodes.length - 1);
  const distanceMap = links
    .filter((l) => l.source.id === centerId)
    .reduce((obj, l) => {
      obj[l.target.id] = Math.max(120, l.distance[soloOrViewpoints] * distanceFactor);
      return obj;
    }, {});
  let hasPositionedCenterNode = false;
  const positions = nodes.reduce((obj, node, i) => {
    if (node.id === centerId) {
      obj[node.id] = {
        x: center.x,
        y: center.y,
        z: center.z,
      };
      hasPositionedCenterNode = true;
    } else {
      const j = hasPositionedCenterNode ? i - 1 : i;
      obj[node.id] = {
        x: center.x + distanceMap[node.id] * Math.sin(j * radiansPerNode + startingRadians),
        y: center.y + distanceMap[node.id] * Math.cos(j * radiansPerNode + startingRadians),
        z: center.z,
      };
    }
    return obj;
  }, {});

  return positions;
}

export function leaderTypeLayout(
  nodes: HeadNode[],
  links: any[],
  soloOrViewpoints: "solo" | "viewpoints" = "solo",
  center: NodePosition = origin(),
): NodePositionMap {
  // Make copies to ensure the simulation is independent
  const nodeCopies = nodes.map((n, i) => {
    return {
      id: n.id,
      index: i,
      name: n.name,
      type: n.type,
      data: n.data,
      x: 0,
      y: 0,
      z: 0,
      vx: 0,
      vy: 0,
      vz: 0,
    };
  });
  const linkCopies = links.map((l, i) => {
    return {
      source: l.source?.id || l.source,
      target: l.target?.id || l.target,
      distance: l.distance,
      index: i,
    };
  });
  if (nodeCopies.length < 3) return linedUpHorizontally(nodeCopies);
  const simulation = forceSimulation(nodeCopies).numDimensions(3);
  const linkForce = forceLink(linkCopies)
    .id((n) => n.id)
    .distance((l) => l.distance[soloOrViewpoints] * 2);
  const manyBodyForce = forceManyBody();
  manyBodyForce.strength(-2200);
  simulation.force("link", linkForce);
  simulation.force("charge", manyBodyForce);
  simulation.force("center", forceCenter());
  simulation.tick(200); // TODO: What is the minimum number of ticks required to get a stable layout?
  const positions = simulation.nodes().reduce((obj, node) => {
    obj[node.id] = {
      x: center.x + node.x,
      y: center.y + node.y,
      z: center.z + node.z,
    };
    return obj;
  }, {});
  return positions;
}
